import moment from 'moment'

import {DEFAULT_SLOT_INTERVAL_MINUTES} from '../consts'
import {SlotBusinessHours, TimeSlot} from '../types'

/**
 * Configuration for generating time slots
 */
export type GenerateSlotsConfig = {
  /** Business hours start (HH:mm) */
  earliestPossibleLoadTime: string
  /** Business hours end (HH:mm) */
  latestPossibleLoadTime: string
  /** Interval between slots in minutes */
  intervalMinutes?: number
}

/**
 * Configuration for applying availability to slots
 */
export type ApplyAvailabilityConfig = {
  /** Earliest possible load time (HH:mm) */
  earliestPossibleLoadTime: string
  /** Latest possible load time (HH:mm) */
  latestPossibleLoadTime: string
}

/**
 * Generates time slot strings based on business hours.
 * Simple generation - just times in HH:mm format.
 * 
 * @param config - Configuration with business hours
 * @returns Array of time strings ["06:00", "06:30", "07:00", ...]
 */
export const generateTimeSlots = (config: GenerateSlotsConfig): string[] => {
  const {
    earliestPossibleLoadTime,
    latestPossibleLoadTime,
    intervalMinutes = DEFAULT_SLOT_INTERVAL_MINUTES
  } = config

  const startTime = moment(earliestPossibleLoadTime, 'HH:mm')
  const endTime = moment(latestPossibleLoadTime, 'HH:mm')

  const slots: string[] = []
  const current = startTime.clone()

  while (current.isSameOrBefore(endTime)) {
    slots.push(current.format('HH:mm'))
    current.add(intervalMinutes, 'minutes')
  }

  return slots
}

/**
 * Applies availability and load hours information to time slots.
 * 
 * Transforms simple time strings into full TimeSlot objects with:
 * - isAvailable: whether slot is in availableSlots list
 * - isOutsideLoadHours: whether slot is outside load time window
 * 
 * @param slots - Time strings from generateTimeSlots
 * @param availableSlots - Available slots from API (HH:mm format)
 * @param loadHoursConfig - Load hours configuration
 * @returns Full TimeSlot objects with all metadata
 */
export const applyAvailability = (
  slots: string[],
  availableSlots: string[],
  loadHoursConfig: ApplyAvailabilityConfig
): TimeSlot[] => {
  const {earliestPossibleLoadTime, latestPossibleLoadTime} = loadHoursConfig
  
  return slots.map((time) => {
    const slotMoment = moment(time, 'HH:mm')
    const earliest = moment(earliestPossibleLoadTime, 'HH:mm')
    const latest = moment(latestPossibleLoadTime, 'HH:mm')
    
    return {
      time,
      isAvailable: availableSlots.includes(time),
      isOutsideLoadHours: slotMoment.isBefore(earliest) || slotMoment.isAfter(latest)
    }
  })
}

/**
 * Checks if selected slot is in available slots list
 */
export const isSlotAvailable = (
  selectedSlot: string | moment.Moment,
  availableSlots: string[]
): boolean => {
  const selectedTime = moment(selectedSlot).format('HH:mm')
  return availableSlots.includes(selectedTime)
}

/**
 * Checks if slot time is outside business hours
 */
export const isOutOfBusinessHours = (
  slotTime: string,
  businessHours: SlotBusinessHours
): boolean => {
  const timeString =
    slotTime.includes(':') && slotTime.length === 5 ? slotTime : moment(slotTime).format('HH:mm')

  const {earliestPossible, latestPossible} = businessHours

  return timeString < earliestPossible || timeString > latestPossible
}

